import { CompanyShareReportComponent } from '../company-share-report/company-share-report.component';
import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpService } from 'src/app/http.service';

interface ReportColumn {
  key: string;
  title: string;
}

@Component({
  selector: 'app-consolidated-daily-sales-report',
  standalone: true,
  imports: [CommonModule, FormsModule, CompanyShareReportComponent],
  templateUrl: './consolidated-daily-sales-report.component.html',
  styleUrls: ['./consolidated-daily-sales-report.component.scss'],
})
export class ConsolidatedDailySalesReportComponent implements OnInit {
  public filterMonth = this.formatPeriod(new Date());
  public months: Array<{ value: string; label: string }> = [];
  public is_mario = false;
  public companies: any[] = [];
  public startPeriod = '';
  public shareholder = false;
  public shareSplit: any;
  public days: any[] = [];
  public weeks: any[][] = [];
  public parentCompany: any = {};
  public is_loading = false;
  public error = '';
  public savingDate = '';
  public saveMessage = '';
  private requestId = 0;

  public moneyColumns: ReportColumn[] = [
    { key: 'debit', title: 'Debit 进钱' },
    { key: 'credit', title: 'Credit 出钱' },
    { key: 'gw_fees', title: 'GW Fees' },
    { key: 'balance', title: 'Balance' },
    { key: 'bonus', title: 'Bonus' },
  ];
  public bankColumns: ReportColumn[] = [
    { key: 'bank_in', title: 'Bank In Capital' },
    { key: 'bank_out', title: 'Bank Withdraw' },
  ];

  constructor(private http: HttpService) {
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    this.months = [today, lastMonth].map(date => ({
      value: this.formatPeriod(date),
      label: ('0' + (date.getMonth() + 1)).slice(-2) + '-' + date.getFullYear(),
    }));

    const access: any = this.http.getAccess();
    this.is_mario = access?.is_mario == '1';
  }

  ngOnInit(): void {
    void this.load();
  }

  monthChange(value: Event | string): void {
    if (value instanceof Event) {
      this.filterMonth = (value.target as HTMLInputElement).value;
    } else if (value) {
      this.filterMonth = value;
    }
    void this.load();
  }

  async load(refresh = false) {
    const currentRequest = ++this.requestId;
    this.is_loading = true;
    this.error = '';
    try {
      const response: any = await this.http.post('/admin/consolidated-daily-sales-report/get', {
        period: this.filterMonth,
        refresh,
      });
      if (currentRequest !== this.requestId) return;

      if (!response?.status) {
        this.clearReport();
        this.error = response?.message || 'Unable to load consolidated daily sales report.';
      } else {
        this.startPeriod = response.start_period || '';
        this.shareholder = response.shareholder === true;
        if (this.shareholder && this.startPeriod) {
          this.months = [];
          const month = new Date();
          month.setDate(1);
          while (this.formatPeriod(month) >= this.startPeriod) {
            this.months.push({ value: this.formatPeriod(month), label: ('0' + (month.getMonth() + 1)).slice(-2) + '-' + month.getFullYear() });
            month.setMonth(month.getMonth() - 1);
          }
        }
        this.companies = response.companies || [];
        this.days = response.days || [];
        this.shareSplit = response.share_split;
        this.weeks = response.weeks || [];
        this.parentCompany = response.parent_company || {};
      }
    } catch {
      if (currentRequest !== this.requestId) return;
      this.clearReport();
      this.error = 'Unable to load consolidated daily sales report.';
    } finally {
      if (currentRequest === this.requestId) {
        this.is_loading = false;
      }
    }
  }

  total(rows: any[], key: string): number {
    return (rows || [])
      .reduce((sum, row) => sum + this.number(row[key]), 0);
  }

  async saveAdjustment(day: any) {
    if (this.savingDate || !day.adjustment_edit?.can_edit) return;
    this.savingDate = day.date;
    this.saveMessage = '';
    try {
      const result: any = await this.http.post('/admin/consolidated-daily-sales-report/adjustment/save', {
        token: this.http.getAccess()?.token,
        date: day.date, scope: day.adjustment_edit.scope,
        expected_revision: day.adjustment_edit.revision,
        adj_in: day.adjustment_edit.adj_in, adj_out: day.adjustment_edit.adj_out,
      });
      if (result?.status) {
        await this.load(true);
        this.saveMessage = 'Consolidated adjustment saved. Branch figures are unchanged.';
      } else this.saveMessage = result?.message || 'Unable to save adjustment.';
    } catch {
      this.saveMessage = 'Unable to save adjustment. Reload the report before retrying.';
    } finally { this.savingDate = ''; }
  }

  private number(value: unknown): number {
    const parsed = parseFloat(String(value ?? 0));
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private clearReport(): void {
    this.companies = [];
    this.days = [];
    this.weeks = [];
    this.parentCompany = {};
  }

  private formatPeriod(date: Date): string {
    return date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2);
  }
}
